/**
 * The zone this module's absolute timestamps are read in.
 *
 * Pinned to India Standard Time on a 12-hour clock, by request: the header's
 * Detected stamp, the "Updated {when}" line beside the tabs, and the Reasoning
 * timeline's entries. Every RELATIVE time in the module still follows the org's
 * configured zone through `formatDateTime`'s default.
 *
 * ── It lives here because the screen became four files ──────────────────
 * It was a private constant in `AnomalyDetailPage`, with a note saying it was
 * deliberately not exported so it could not spread. Splitting the tabs out took
 * two of its readers into their own files, and the choice was then between one
 * shared constant and three copies of an options object that must agree. The
 * intent of that note is kept by scope rather than by privacy: this file is the
 * anomalies module's, and nothing outside `pages/anomalies` may import it.
 */
const RAIL_TIME_ZONE = 'Asia/Kolkata'

/**
 * The pinned stamps share one options object so they cannot drift apart.
 *
 * `hour12` is passed explicitly rather than left to the locale, because
 * `formatDateTime` merges the org's own `hour12` in first and an org set to
 * 24-hour would otherwise win here.
 */
export const IST_STAMP = {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: RAIL_TIME_ZONE,
  hour12: true,
}

/**
 * The DATE alone, and the TIME alone — the pair the event facts are built from.
 *
 * Four stamps describe one anomaly's window (start, end, detected, last
 * detected) and they almost always fall in the same minute of the same day, so
 * printing the date four times is four repetitions of the only part that does
 * not vary. The date is stated once and the times run beside it.
 *
 * ── The times carry SECONDS, and that is not detail for its own sake ────
 * `IST_STAMP` is `timeStyle: 'short'`. An ingestion outage runs 54 seconds: its
 * window end and its detection land in the same minute, and at minute precision
 * all four stamps print the same value — a row of four identical times, which
 * reads as a bug. `last_detected_at` can also fall AFTER `window_end`, which is
 * a real and interesting fact about an extending anomaly and is invisible
 * without seconds.
 */
export const IST_DATE = {
  dateStyle: 'medium',
  timeZone: RAIL_TIME_ZONE,
}

export const IST_TIME = {
  timeStyle: 'medium',
  timeZone: RAIL_TIME_ZONE,
  hour12: true,
}

/** Whether two instants land on the same day IN THE ZONE the stamps are read in. */
export function sameDayInZone(a, b) {
  if (!a || !b) return false
  const day = (value) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: RAIL_TIME_ZONE }).format(new Date(value))
  return day(a) === day(b)
}

/**
 * An axis tick's clock: time only, with seconds, no date.
 *
 * The chart's x axis spans one anomaly window — seconds to minutes — so every
 * tick falls on the same day, and five dates across a 26rem card is five
 * repetitions of a fact the header already states once. Seconds are the point:
 * at minute precision a 54-second window labels every tick identically.
 */
export const IST_TICK = {
  timeStyle: 'medium',
  timeZone: RAIL_TIME_ZONE,
  hour12: true,
}
